import { css, styled } from "@mui/material";

import { getColor } from "@hooks";
import { cssForHeaderWithLinearGradient, cssPropertiesToString } from "@utils";

import CornerBorder from "./svgs/corner_border.svg";
import Pin from "./svgs/pin.svg";
import PinDisabled from "./svgs/pin_disabled.svg";
import PinLight from "./svgs/pin_light.svg";
import PinLightDisabled from "./svgs/pin_light_disabled.svg";

export interface CopyBlockStyledProps {
  top: number;
  left: number;
}

interface EnabledDisabledProps {
  enabled: boolean;
}

export const CopyBlockStyled = styled("div", {
  shouldForwardProp: prop =>
    !["top", "left", "enabled"].includes(prop as string),
})<CopyBlockStyledProps & EnabledDisabledProps>(
  ({ top, left, enabled }) => css`
    position: absolute;
    width: 380px;

    top: ${top + 182}px;
    left: ${left}px;

    :before {
      content: "";
      position: absolute;
      width: 800px;
      height: 600px;
      top: -600px;
      left: -100px;
      opacity: ${enabled ? "0.3" : "0"};
      transition: opacity 1s;
      z-index: -1;

      background:
        radial-gradient(
          50% 36.46% at 50% 36.46%,
          rgba(75, 113, 250, 0.08) 0%,
          rgba(5, 5, 11, 0) 100%
        ),
        radial-gradient(
          50% 38.81% at 50% 61.19%,
          rgba(150, 197, 240, 0.08) 0%,
          rgba(24, 27, 37, 0) 100%
        );
    }
  `,
);

export const NumberStyled = styled("span", {
  shouldForwardProp: prop => !["enabled"].includes(prop as string),
})<EnabledDisabledProps>(
  ({ theme, enabled }) => css`
    font-family:
      Work Sans,
      sans-serif;
    font-size: 180px;
    font-weight: 700;
    line-height: 211.14px;
    letter-spacing: -0.02em;
    text-align: left;
    margin-bottom: -40px;

    ${theme.palette.mode === "light"
      ? css`
          background: linear-gradient(
            143.8deg,
            #52586680 0.14%,
            #f1f3f680 58.85%
          );

          background: linear-gradient(143.8deg, #525866 0.14%, #f1f3f6 58.85%);

          @property --start-color {
            syntax: "<color>";
            inherits: false;
            initial-value: #52586680;
          }

          @property --stop-color {
            syntax: "<color>";
            inherits: false;
            initial-value: #f1f3f680;
          }

          --start-color: ${!enabled ? "#52586680" : "#525866"};
          --stop-color: ${!enabled ? "#f1f3f680" : "#f1f3f6"};

          ${cssForHeaderWithLinearGradient(
            theme,
            "linear-gradient(143.8deg, var(--start-color) 0.14%, var(--stop-color) 58.85%)",
          )}

          transition: --start-color 1s, --stop-color 1s;
        `
      : css`
          @property --stop-color {
            syntax: "<color>";
            inherits: false;
            initial-value: #181b25;
          }

          @property --stop-percent {
            syntax: "<percentage>";
            inherits: false;
            initial-value: 50.59%;
          }

          --stop-color: ${enabled ? "#2B303B" : "#181B25"};
          --stop-percent: ${enabled ? "79.38%" : "50.59%"};

          ${cssForHeaderWithLinearGradient(
            theme,
            "linear-gradient(180deg, #7583A1 21.8%, #2B303B var(--stop-percent), var(--stop-color) 79.38%)",
          )}

          transition: --stop-color 1s, --stop-percent 1s;
        `}
  `,
);

export const TitleStyled = styled("span", {
  shouldForwardProp: prop => !["enabled"].includes(prop as string),
})<EnabledDisabledProps>(
  ({ theme, enabled }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_xxlarge_semibold)}
    color: ${enabled
      ? getColor(x => x.tokens.text["strong-950"], theme)
      : getColor(x => x.tokens.text["sub-600"], theme)};
    transition: color 1s;
  `,
);

export const DescriptionStyled = styled("span", {
  shouldForwardProp: prop => !["enabled"].includes(prop as string),
})<EnabledDisabledProps>(
  ({ theme, enabled }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_medium_medium)}
    color: ${getColor(x => x.tokens.text["sub-600"], theme)};
    position: relative;
    max-height: ${enabled ? "100px" : "0"};
    opacity: ${enabled ? 1 : 0};
    transition:
      max-height 1s,
      opacity 1s;
  `,
);

export const UpperCorderBorder = styled(CornerBorder)`
  position: absolute;
  top: -24px;
  right: -20px;
`;

export const LowerCorderBorder = styled(CornerBorder)`
  position: absolute;
  bottom: -16px;
  left: -20px;
  transform: rotate(180deg);
`;

export const CopyBlockContent = styled("div", {
  shouldForwardProp: prop => !["enabled"].includes(prop as string),
})<EnabledDisabledProps>(
  ({ enabled }) => css`
    left: 0px;
    bottom: ${enabled ? "140px" : "100px"};
    position: absolute;
    display: flex;
    flex-direction: column;
    transition: bottom 1s;
  `,
);

export const stylePin = (component: any) =>
  styled(component, {
    shouldForwardProp: prop => !["enabled"].includes(prop as string),
  })<EnabledDisabledProps>(
    ({ enabled }) => css`
      position: absolute;
      bottom: 0px;
      left: 12%;
      transform: translateX(-50%);
      opacity: ${enabled ? 1 : 0};
      transition: opacity 1s;
    `,
  );

export const PinDarkStyled = stylePin(Pin);
export const PinLightStyled = stylePin(PinLight);
export const PinDisabledDarkStyled = stylePin(PinDisabled);
export const PinDisabledLightStyled = stylePin(PinLightDisabled);

export const FixedCopyContainer = styled("div")`
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
  z-index: 100;
`;
